import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getFeeAnalytics,
  getFeeAnalyticsPortal,
} from "../../api/fees/feeAnalyticsApi";
import KPIGroup from "./KPIGroup";
import ChartCard from "./ChartCard";
import DonutChart from "./DonutChart";
import BarChart from "./BarChart";
import AreaChart from "./AreaChart";
import GroupedBarChart from "./GroupedBarChart";
import { useToast } from "../../context/ToastContext";

/* Fee-specific palette — distinct from staff/student indigo */
const FEE_COLORS = {
  teal: "#0D9488",
  emerald: "#059669",
  amber: "#D97706",
  rose: "#E11D48",
  violet: "#7C3AED",
  sky: "#0284C7",
  slate: "#64748B",
};

const MODE_COLORS = [
  FEE_COLORS.emerald,
  FEE_COLORS.teal,
  FEE_COLORS.sky,
  FEE_COLORS.violet,
  FEE_COLORS.amber,
];

const STATUS_COLORS = [
  FEE_COLORS.emerald, // Paid
  FEE_COLORS.amber, // Partial
  FEE_COLORS.slate, // Pending
  FEE_COLORS.rose, // Overdue
];

const PERIOD_OPTIONS = [
  { value: "month", label: "This Month" },
  { value: "year", label: "This Year" },
  { value: "all", label: "Last 12 Months" },
];

const formatINR = (n) => {
  if (n == null) return "—";
  return `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

const LoadingState = () => (
  <div className="flex h-40 items-center justify-center">
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-teal-600 border-t-transparent" />
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className="flex flex-col items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-6 text-center">
    <p className="text-sm text-rose-700">{message || "Failed to load analytics"}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="rounded-md bg-rose-600 px-3 py-1 text-xs text-white hover:bg-rose-700"
      >
        Retry
      </button>
    )}
  </div>
);

const FeeAnalytics = ({ isPortal = false }) => {
  const [period, setPeriod] = useState("month");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { period };
      if (classId) params.classId = classId;
      if (sectionId) params.sectionId = sectionId;

      const result = isPortal
        ? await getFeeAnalyticsPortal(params)
        : await getFeeAnalytics(params);
      setData(result);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, [period, classId, sectionId, isPortal]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const classes = data?.filterOptions?.classes || [];
  const allSections = data?.filterOptions?.sections || [];
  const sections = useMemo(() => {
    if (!classId) return allSections;
    return allSections.filter((s) => String(s.classId) === String(classId));
  }, [allSections, classId]);

  const handleClassChange = (e) => {
    setClassId(e.target.value);
    setSectionId("");
  };

  const kpis = data?.kpis || {};
  const distributions = data?.distributions || {};
  const trends = data?.trends || {};

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <h2 className="text-sm font-semibold text-slate-700 dark:text-neutral-200">Fee Overview</h2>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={classId}
            onChange={handleClassChange}
            className="rounded-md border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-2 py-1 text-xs text-slate-700 dark:text-neutral-200"
          >
            <option value="">All Classes</option>
            {classes.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>

          <select
            value={sectionId}
            onChange={(e) => setSectionId(e.target.value)}
            className="rounded-md border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-2 py-1 text-xs text-slate-700 dark:text-neutral-200"
            disabled={!sections.length}
          >
            <option value="">All Sections</option>
            {sections.map((s) => (
              <option key={s._id} value={s._id}>
                {s.name}
              </option>
            ))}
          </select>

          <div className="flex gap-1">
            {PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setPeriod(opt.value)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${ period === opt.value ? "bg-teal-700 text-white" : "bg-slate-100 dark:bg-neutral-900 text-slate-600 dark:text-neutral-300 hover:bg-slate-200" }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && <LoadingState />}
      {!loading && error && <ErrorState message={error} onRetry={fetchData} />}

      {!loading && !error && data && (
        <>
          {/* Money KPIs */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <KPIGroup
              title="Outstanding Position"
              items={[
                { label: "Billed", value: formatINR(kpis.totalBilled) },
                {
                  label: "Collected",
                  value: formatINR(kpis.totalCollectedLedger),
                  tone: "success",
                },
                {
                  label: "Due",
                  value: formatINR(kpis.totalDue),
                  tone: kpis.totalDue > 0 ? "warning" : "default",
                },
                {
                  label: "Rate",
                  value:
                    kpis.collectionRate != null
                      ? `${kpis.collectionRate}%`
                      : "—",
                  tone:
                    kpis.collectionRate >= 80
                      ? "success"
                      : kpis.collectionRate >= 50
                      ? "warning"
                      : "danger",
                },
              ]}
            />
            <KPIGroup
              title="This Period"
              items={[
                {
                  label: "Collected",
                  value: formatINR(kpis.periodCollected),
                  tone: "success",
                },
                { label: "Receipts", value: kpis.receiptCount },
                {
                  label: "Avg receipt",
                  value: formatINR(kpis.avgReceipt),
                },
                {
                  label: "Today",
                  value: formatINR(kpis.todayCollected),
                  sub: kpis.todayReceipts
                    ? `${kpis.todayReceipts} receipts`
                    : null,
                },
              ]}
            />
            <KPIGroup
              title="Risk & Adjustments"
              items={[
                {
                  label: "Overdue",
                  value: kpis.overdueCount,
                  tone: "danger",
                  sub: formatINR(kpis.overdueAmount),
                },
                {
                  label: "Pending bills",
                  value: kpis.pendingCount,
                  tone: "muted",
                },
                {
                  label: "Discounts",
                  value: formatINR(kpis.totalDiscount),
                },
                {
                  label: "Refunds",
                  value: formatINR(kpis.totalRefunded),
                  sub: kpis.refundCount ? `${kpis.refundCount} cases` : null,
                  tone: kpis.totalRefunded > 0 ? "warning" : "default",
                },
              ]}
            />
          </div>

          {/* Unique charts for fee */}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <ChartCard title="Collected vs Due by Class">
              <GroupedBarChart
                data={distributions.byClass || []}
                series={[
                  {
                    key: "collected",
                    name: "Collected",
                    color: FEE_COLORS.emerald,
                  },
                  { key: "due", name: "Due", color: FEE_COLORS.amber },
                ]}
                height={220}
                layout="horizontal"
              />
            </ChartCard>

            <ChartCard title="Payment Mode Mix">
              <DonutChart
                data={distributions.byPaymentMode || []}
                colors={MODE_COLORS}
                height={220}
              />
            </ChartCard>
          </div>

          <ChartCard title="Collection Trend">
            <AreaChart
              data={trends.collectionTrend || []}
              height={200}
              dataKey="value"
              xKey="period"
              color={FEE_COLORS.teal}
            />
          </ChartCard>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <ChartCard title="Bill Status (count)">
              <DonutChart
                data={(distributions.byStatus || []).map((s) => ({
                  name: s.name,
                  value: s.value,
                }))}
                colors={STATUS_COLORS}
                height={200}
              />
            </ChartCard>

            <ChartCard title="Top Fee Heads (billed)">
              <BarChart
                data={distributions.byFeeHead || []}
                layout="horizontal"
                height={200}
                color={FEE_COLORS.violet}
                categoryWidth={110}
              />
            </ChartCard>
          </div>
        </>
      )}
    </div>
  );
};

export default FeeAnalytics;
